import { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { ConfirmModal, ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { Spinner } from '@shared/components/ui/Spinner/Spinner';
import { useRateDetail } from '../../hooks/useRateDetail';
import { useChangeRateStatus } from '../../hooks/useChangeRateStatus';
import { useActiveOwnBoxes } from '../../hooks/useActiveOwnBoxes';
import { RateItemsTable } from '../display/RateItemsTable';
import { RateStatusBadge } from '../display/RateStatusBadge';
import { AddRateItemModalRef } from '../modals/AddRateItemModal';
import { EditPackagingPricesModalRef } from '../modals/EditPackagingPricesModal';

interface RateDetailContainerProps {
  addItemModalRef: React.RefObject<AddRateItemModalRef | null>;
  editPackagingModalRef: React.RefObject<EditPackagingPricesModalRef | null>;
}

export const RateDetailContainer = ({ addItemModalRef, editPackagingModalRef }: RateDetailContainerProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('ratesModule');
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const canView = hasPermission('rates.view');
  const canManage = hasPermission('rates.manage');

  const rateId = id ? parseInt(id, 10) : null;

  const { rate, isLoading, error } = useRateDetail(rateId);
  const { mutateAsync: changeStatus } = useChangeRateStatus();
  const { ownBoxes } = useActiveOwnBoxes();

  const handleAddItem = useCallback(() => {
    addItemModalRef.current?.open();
  }, [addItemModalRef]);

  const handleEditPackagingPrices = useCallback(() => {
    editPackagingModalRef.current?.open();
  }, [editPackagingModalRef]);

  const handleDeactivate = useCallback(() => {
    if (!rate) return;

    confirmModalRef.current?.open(
      async () => {
        try {
          await changeStatus(rate.id);
          toast({
            title: t('toast.deactivateSuccess'),
          });
        } catch (error: any) {
          toast({
            title: t('toast.deactivateError'),
            description: error?.response?.data?.message || error.message,
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('modal.deactivate.title'),
      t('modal.deactivate.description', { name: rate.name }),
      t('modal.deactivate.confirm'),
      t('modal.deactivate.cancel'),
    );
  }, [rate, changeStatus, toast, t]);

  // Breadcrumbs configuration with badge
  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.invoices', 'Facturación') },
      { label: t('list.title'), path: '/invoices/rates' },
      {
        label: rate ? (
          <div className="flex items-center gap-2">
            <span className="text-title">{rate.name}</span>
            <RateStatusBadge isActive={rate.is_active} />
          </div>
        ) : (
          '...'
        ),
      },
    ],
    [t, rate],
  );

  // Header actions configuration
  const headerActions = useMemo(() => {
    if (!rate || !canManage) return [];

    const actions = [
      {
        id: 'add-item',
        label: t('actions.addItem'),
        onClick: handleAddItem,
        variant: 'primary' as const,
      },
    ];

    if (ownBoxes.length > 0) {
      actions.push({
        id: 'edit-packaging-prices',
        label: t('actions.editPackagingPrices'),
        onClick: handleEditPackagingPrices,
        variant: 'outline' as const,
      });
    }

    if (rate.is_active) {
      actions.push({
        id: 'deactivate',
        label: t('actions.deactivate'),
        onClick: handleDeactivate,
        variant: 'outline' as const,
      });
    }

    return actions;
  }, [rate, canManage, t, handleAddItem, handleEditPackagingPrices, handleDeactivate, ownBoxes.length]);

  // Register with navigation system
  usePageNavigation({ breadcrumbs, headerActions });

  if (!canView) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !rate) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 px-4">
        <h2 className="text-lg font-medium text-title mb-2">{t('detail.error')}</h2>
        <p className="text-sm text-subtext mb-4">{t('detail.errorDescription')}</p>
        <button onClick={() => navigate('/invoices/rates')} className="text-primary hover:underline">
          {t('detail.backToList')}
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Items table */}
      <RateItemsTable items={rate.items} ownBoxes={ownBoxes} />

      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
